/* ============================================================
   js/firebase.js  —  Shared Firebase init, Auth & Firestore
   Used by: login.html, cart.html, admin.html, studio.html
   ============================================================ */

import { initializeApp }          from "https://www.gstatic.com/firebasejs/10.12.2/firebase-app.js";
import { getAuth,
         onAuthStateChanged,
         signInWithEmailAndPassword,
         createUserWithEmailAndPassword,
         signInWithPopup,
         signInWithRedirect,
         getRedirectResult,
         GoogleAuthProvider,
         signOut,
         updateProfile }          from "https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js";
import { getFirestore,
         collection, doc,
         setDoc, getDoc, addDoc,
         getDocs, updateDoc,
         query, where, orderBy, limit,
         increment,
         serverTimestamp }        from "https://www.gstatic.com/firebasejs/10.12.2/firebase-firestore.js";
import { getStorage,
         ref as storageRef,
         uploadString,
         getDownloadURL }         from "https://www.gstatic.com/firebasejs/10.12.2/firebase-storage.js";

/* ── Config ── */
const firebaseConfig = {
  apiKey:            "AIzaSyBReu5V7XZO-gWf7PbDcg_D9WQoOYbUyLI",
  authDomain:        "chaosstudio-a627f.firebaseapp.com",
  projectId:         "chaosstudio-a627f",
  storageBucket:     "chaosstudio-a627f.firebasestorage.app",
  messagingSenderId: "703906370564",
  appId:             "1:703906370564:web:434cb8dbb5fbee14ca5151"
};

const app  = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db   = getFirestore(app);
const storage = getStorage(app);
const googleProvider = new GoogleAuthProvider();

/* ============================================================
   AUTH HELPERS
   ============================================================ */

/** Sign up with email + password, then create Firestore user doc */
async function signUp(name, email, password) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(cred.user, { displayName: name });
  await _createUserDoc(cred.user, name);
  return cred.user;
}

/** Sign in with email + password */
async function signIn(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

/** Sign in with Google — POPUP mode */
async function signInGoogle() {
  console.log('[Auth] signInGoogle() — popup mode');
  googleProvider.setCustomParameters({ prompt: 'select_account' });
  const result = await signInWithPopup(auth, googleProvider);
  await _createUserDoc(result.user, result.user.displayName);
  return result.user;
}

/** No-op kept for backward compatibility (redirect mode not used) */
async function handleGoogleRedirect() {
  return null;
}

/** Sign out */
async function logOut() {
  await signOut(auth);
}

/** Create user doc in Firestore if it doesn't exist yet */
async function _createUserDoc(user, name) {
  const ref  = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    // Auto-set role to 'admin' if email is in the whitelist
    const role = ADMIN_EMAILS.includes(user.email) ? 'admin' : 'customer';
    await setDoc(ref, {
      uid:       user.uid,
      name:      name || '',
      email:     user.email,
      phone:     '',
      address:   '',
      createdAt: serverTimestamp(),
      role,
    });
  }
}

/** Get current user's Firestore profile */
async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? snap.data() : null;
}

/* ── Admin email list ──────────────────────────────────────────
   Add the owner's email here. These accounts are ALWAYS treated
   as admin, even before the Firestore role field is set.
   To add more admins later, just add their email to this array.
───────────────────────────────────────────────────────────── */
const ADMIN_EMAILS = [
  'aditideshpande.sy@gmail.com',
  'aditideshpande-sy@gmail.com',  // alternate spelling just in case
];

/** Returns true if the signed-in user is an admin.
 *  Three ways to be admin:
 *  1. Their email is in the ADMIN_EMAILS whitelist above (hardcoded, always works)
 *  2. Their Firestore users doc has  role: 'admin'
 *  3. Email contains 'aditideshpande' (owner fallback — remove after setup)
 */
async function isAdmin(user) {
  if (!user) return false;
  // Whitelist check — case-insensitive
  const email = (user.email || '').toLowerCase().trim();
  if (ADMIN_EMAILS.map(e => e.toLowerCase()).includes(email)) return true;
  // Owner email pattern fallback
  if (email.includes('aditideshpande')) return true;
  // Firestore role check
  const profile = await getUserProfile(user.uid);
  return profile?.role === 'admin';
}

/** Promote a user to admin role in Firestore */
async function promoteToAdmin(uid) {
  await updateDoc(doc(db, 'users', uid), { role: 'admin' });
}

/** Update user profile fields */
async function updateUserProfile(uid, fields) {
  await updateDoc(doc(db, 'users', uid), fields);
}

/* ============================================================
   GENERATION CREDITS
   ─────────────────────────────────────────────────────────────
   Each user gets 5 free generation credits on first use.
   After a successful paid order they receive 5 more credits.
   Each time generate() fires it consumes 1 credit.
   Admin accounts always have unlimited credits.

   Firestore field:  users/{uid}.gen_credits  (number, default 5)
   ============================================================ */

const GEN_CREDITS_FREE        = 5;   // granted on first use / default
const GEN_CREDITS_PER_ORDER   = 5;   // granted after each paid order

/**
 * getGenCredits(uid)
 * Returns the number of generation credits remaining for the user.
 * If the field doesn't exist yet it initialises it to GEN_CREDITS_FREE.
 */
async function getGenCredits(uid) {
  if (!uid) return 0;
  const ref  = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    // Create profile skeleton with credits
    await setDoc(ref, { gen_credits: GEN_CREDITS_FREE, createdAt: serverTimestamp() }, { merge: true });
    return GEN_CREDITS_FREE;
  }
  const data = snap.data();
  if (typeof data.gen_credits !== 'number') {
    // Field missing — initialise it
    await updateDoc(ref, { gen_credits: GEN_CREDITS_FREE });
    return GEN_CREDITS_FREE;
  }
  return data.gen_credits;
}

/**
 * consumeGenCredit(uid)
 * Atomically decrements the credit by 1.
 * Returns { ok: true, remaining: N }  on success.
 * Returns { ok: false, remaining: 0 } when already at 0.
 * Throws if uid is missing.
 */
async function consumeGenCredit(uid) {
  if (!uid) throw new Error('consumeGenCredit: uid required');
  const ref     = doc(db, 'users', uid);
  const snap    = await getDoc(ref);
  let current   = GEN_CREDITS_FREE;

  if (!snap.exists()) {
    await setDoc(ref, { gen_credits: GEN_CREDITS_FREE, createdAt: serverTimestamp() }, { merge: true });
    current = GEN_CREDITS_FREE;
  } else {
    const d = snap.data();
    current = typeof d.gen_credits === 'number' ? d.gen_credits : GEN_CREDITS_FREE;
    if (typeof d.gen_credits !== 'number') {
      await updateDoc(ref, { gen_credits: GEN_CREDITS_FREE });
      current = GEN_CREDITS_FREE;
    }
  }

  if (current <= 0) {
    return { ok: false, remaining: 0 };
  }

  const newVal = current - 1;
  await updateDoc(ref, { gen_credits: newVal });
  return { ok: true, remaining: newVal };
}

/**
 * grantGenCreditsAfterOrder(uid)
 * Adds GEN_CREDITS_PER_ORDER credits to the user's account.
 * Called from saveOrder() after a successful payment.
 */
async function grantGenCreditsAfterOrder(uid) {
  if (!uid) return;
  const ref  = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, { gen_credits: GEN_CREDITS_FREE + GEN_CREDITS_PER_ORDER, createdAt: serverTimestamp() }, { merge: true });
    return;
  }
  const current = typeof snap.data().gen_credits === 'number'
    ? snap.data().gen_credits
    : GEN_CREDITS_FREE;
  await updateDoc(ref, { gen_credits: current + GEN_CREDITS_PER_ORDER });
  console.log(`[Credits] +${GEN_CREDITS_PER_ORDER} credits granted to ${uid}. New total: ${current + GEN_CREDITS_PER_ORDER}`);
}

/* ============================================================
   STORAGE HELPERS
   ============================================================ */

/**
 * Upload a base64 data URL to Firebase Storage.
 * Returns a public download URL, or the original dataUrl as fallback.
 *
 * path:    e.g. 'designs/user123/studio-1234567890.jpg'
 * dataUrl: 'data:image/png;base64,...'
 */
async function uploadDesignImage(dataUrl, fileName) {
  if (!dataUrl || !dataUrl.startsWith('data:')) {
    console.warn('[Storage] uploadDesignImage: not a data URL, skipping');
    return null;
  }
  const user = auth.currentUser;
  const ts   = Date.now();
  const name = fileName || ('design-' + ts);

  // Try two paths: user-scoped first, then public fallback
  const paths = user
    ? [`designs/${user.uid}/${name}.png`, `designs/public/${name}-${ts}.png`]
    : [`designs/guest/${name}-${ts}.png`, `designs/public/${name}-${ts}.png`];

  for (const path of paths) {
    try {
      console.log('[Storage] Uploading to path:', path, '| user:', user?.email || 'guest');
      const ref = storageRef(storage, path);
      await uploadString(ref, dataUrl, 'data_url');
      const url = await getDownloadURL(ref);
      console.log('[Storage] ✅ Upload success:', url.slice(0, 80) + '…');
      return url;                // ← success, return immediately
    } catch (err) {
      console.warn(`[Storage] ⚠️ Upload failed for path "${path}" — ${err.code}: ${err.message}`);
      // If it's NOT a permission error, stop retrying (quota/network issue)
      if (err.code && !err.code.includes('unauthorized') && !err.code.includes('permission')) {
        console.error('[Storage] ❌ Non-recoverable upload error:', err.code);
        break;
      }
      // Otherwise try the next path
    }
  }

  console.error(
    '[Storage] ❌ All upload paths failed. Falling back to Firestore storage.\n' +
    'To fix Storage: Firebase Console → Storage → Rules → allow write on /designs/**'
  );

  // ── Firestore fallback: store base64 in design_images collection ──
  const firestoreUrl = await saveDesignImageToFirestore(dataUrl);
  if (firestoreUrl) {
    console.log('[Storage] ✅ Saved to Firestore fallback:', firestoreUrl);
    return firestoreUrl;
  }

  return null;
}

/* ============================================================
   ORDER HELPERS
   ============================================================

   DATA MODEL — orders/{orderId}
   ─────────────────────────────
   Core fields (always saved):
     orderId           string   — e.g. "CS1773835354638"
     uid               string?  — Firebase Auth UID (null for guests)
     email             string
     userName          string
     customerName      string
     customerEmail     string
     customerPhone     string
     shippingAddress   string
     notes             string
     items[]           array    — see item schema below
     total             number   — ₹ grand total
     itemCount         number   — sum of qty across all items
     paymentStatus     string   — "paid" | "failed" | "pending"
     orderStatus       string   — "paid" | "processing" | "completed" | "cancelled"
     payment_method    string   — "cashfree" | "upi" | "card" | ...
     transaction_id    string   — Cashfree / UPI txn reference
     payment_verified  bool
     createdAt         Timestamp
     updatedAt         Timestamp

   Item schema (items[] array):
     id           string
     name         string   — full display name
     productType  string   — "tshirt" | "tote" | ...
     designSource string   — "ai" | "manual" | "uploaded"
     vibe         string
     style        string
     color        string
     size         string
     price        number
     qty          number
     imageUrl     string   — Firebase Storage https:// URL for printing
     printThumb   string   — Firebase Storage https:// URL for preview
     designLabel  string   — AI prompt label

   Future fields (add here when fulfillment is built):
     trackingNumber    string   — courier tracking ID
     courierPartner    string   — "Delhivery" | "Shiprocket" | ...
     shippedAt         Timestamp
     deliveredAt       Timestamp
     fulfillmentNotes  string
   ============================================================ */

/**
 * Save a complete order to Firestore after successful payment.
 *
 * @param {Array}  items          - Cart items array
 * @param {Object} shipping       - { name, email, phone, address, notes }
 * @param {number} total          - Grand total in ₹
 * @param {Object} paymentDetails - { payment_method, transaction_id, payment_verified }
 * @param {Object} [extra]        - Optional extra fields (orderId, designSource, etc.)
 * @returns {string} Firestore document ID
 */
async function saveOrder(items, shipping, total, paymentDetails, extra = {}) {
  const user = auth.currentUser;

  // ── URL safety: keep https:// (Storage) and firestore:// (Firestore fallback) URLs.
  // Strip raw base64 data: URIs — too large for Firestore documents.
  const safeUrl = url => {
    if (typeof url !== 'string') return '';
    if (url.startsWith('https://'))      return url; // Firebase Storage URL ✅
    if (url.startsWith('firestore://'))  return url; // Firestore fallback URL ✅
    return ''; // base64 or invalid — strip
  };

  const orderId = String(extra.orderId || '');
  const uid     = user ? user.uid : '';

  // ── If imageUrl is still base64, save to Firestore now and get a reference URL ──
  // Track all design_images doc IDs created so we can backfill the Firestore order ID later.
  const _designImageDocIds = []; // [ docId, ... ]

  const resolvedItems = await Promise.all((items || []).map(async (i) => {
    let imageUrl = i.imageUrl || '';
    const itemId = String(i.id || '');

    // ── Save ONLY the raw design (imageUrl) — NOT the printThumb.
    // printThumb is the full t-shirt composite used for cart UI preview only;
    // the printer needs the clean design artwork, not a mockup with the shirt.
    if (imageUrl.startsWith('data:')) {
      const ref = await saveDesignImageToFirestore(imageUrl, { orderId, itemId, uid });
      imageUrl = ref || '';
      if (ref && ref.startsWith('firestore://design_images/')) {
        _designImageDocIds.push(ref.replace('firestore://design_images/', ''));
      }
    }

    // printThumb is intentionally NOT saved to Firestore:
    //   - it contains the t-shirt mockup background (not the raw design)
    //   - it is only needed for cart/order UI thumbnail display
    //   - keeping it out of Firestore reduces doc size significantly
    return { ...i, imageUrl, printThumb: '' };
  }));

  // ── Detect design source from first item ──
  const firstItem = resolvedItems[0] || {};
  let designSource = extra.designSource || 'manual';
  if (firstItem.fromStudio)            designSource = 'ai';
  else if (firstItem.imageUrl)         designSource = 'uploaded';

  // ── Sanitise every item — plain scalars only, no nested objects ──
  const safeItems = resolvedItems.map(i => ({
    id:           String(i.id          || ''),
    name:         String(i.name        || ''),
    productType:  String(i.productType || 'tshirt'),
    designSource: i.fromStudio ? 'ai' : (i.imageUrl ? 'uploaded' : 'manual'),
    vibe:         String(i.vibe        || ''),
    style:        String(i.style       || ''),
    color:        String(i.color       || ''),
    size:         String(i.size        || ''),
    price:        Number(i.price       || 0),
    qty:          Number(i.qty         || 1),
    imageUrl:     safeUrl(i.imageUrl),
    // printThumb intentionally omitted — it's the t-shirt mockup, not the print file
    designLabel:  String(i.designLabel || i.vibe || ''),
  }));

  const itemCount = safeItems.reduce((sum, i) => sum + i.qty, 0);

  const orderData = {
    /* ── Identity ── */
    orderId:         String(extra.orderId || ''),
    uid:             user ? user.uid   : null,
    email:           user ? user.email : (shipping.email || ''),
    userName:        user ? (user.displayName || '') : (shipping.name || ''),

    /* ── Customer / Shipping ── */
    customerName:    String(shipping.name    || ''),
    customerEmail:   String(shipping.email   || ''),
    customerPhone:   String(shipping.phone   || ''),
    shippingAddress: String(shipping.address || ''),
    notes:           String(shipping.notes   || ''),

    /* ── Items ── */
    items:           safeItems,
    itemCount,
    designSource,   // top-level shortcut for filtering

    /* ── Totals ── */
    total:           Number(total || 0),

    /* ── Payment ── */
    paymentStatus:   'paid',   // paid | failed | pending
    payment_method:  String(paymentDetails?.payment_method  || 'cashfree'),
    transaction_id:  String(paymentDetails?.transaction_id  || ''),
    payment_verified: Boolean(paymentDetails?.payment_verified || false),

    /* ── Order fulfilment status ── */
    // Lifecycle: paid → processing → completed | cancelled
    orderStatus:     'paid',

    /* ── Timestamps ── */
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),

    /* ── Future fulfilment fields (add values when shipping is built) ──
       trackingNumber:   '',
       courierPartner:   '',
       shippedAt:        null,
       deliveredAt:      null,
       fulfillmentNotes: '',
    ── */
  };

  const ref = await addDoc(collection(db, 'orders'), orderData);
  const firestoreOrderId = ref.id;

  // ── Grant generation credits for this paid order ──
  if (user?.uid) {
    grantGenCreditsAfterOrder(user.uid).catch(e =>
      console.warn('[Credits] Failed to grant post-order credits:', e.message)
    );
  }

  // ── Backfill: stamp the Firestore order doc ID onto every design_images doc ──
  // This means each image doc has BOTH the Cashfree orderId AND the Firestore doc ID,
  // so admins can cross-reference from either side.
  if (_designImageDocIds.length) {
    await Promise.all(
      _designImageDocIds.map(docId =>
        updateDoc(doc(db, 'design_images', docId), {
          firestoreOrderId,           // Firestore orders/{id}
          cashfreeOrderId: orderId,   // Cashfree CS_xxx (already stored, but now also here for clarity)
        }).catch(e => console.warn('[Firestore] backfill design_images failed:', e.message))
      )
    );
  }

  return firestoreOrderId;
}

/**
 * Update the orderStatus of an order (admin only).
 * Valid statuses: 'paid' | 'processing' | 'completed' | 'cancelled'
 */
async function updateOrderStatus(orderId, orderStatus) {
  await updateDoc(doc(db, 'orders', orderId), {
    orderStatus,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Update any arbitrary fields on an order (admin only).
 * Used for adding tracking numbers, courier partner, etc.
 */
async function updateOrderFields(orderId, fields) {
  await updateDoc(doc(db, 'orders', orderId), {
    ...fields,
    updatedAt: serverTimestamp(),
  });
}

/** Get all orders for the currently logged-in user */
async function getUserOrders(uid) {
  // Simple where-only query (no composite index needed)
  // Sort client-side by createdAt descending
  const q    = query(collection(db, 'orders'),
                     where('uid', '==', uid));
  const snap = await getDocs(q);
  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  // Sort newest first — handle both Firestore Timestamp and plain numbers
  docs.sort((a, b) => {
    const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt || 0);
    const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt || 0);
    return tb - ta;
  });
  return docs;
}

/** Get ALL orders — admin only */
async function getAllOrders() {
  const q    = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Get all users — admin only */
async function getAllUsers() {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/* ============================================================
   CHAOS BATTLES HELPERS
   ============================================================ */

/**
 * Submit a design entry to the current week's battle.
 * entry = { imageUrl, prompt, vibe, productType, color, weekId? }
 * If entry.weekId is provided, it overrides the auto-calculated current week.
 */
async function submitBattleEntry(entry) {
  const user = auth.currentUser;
  // Allow the caller to pass a custom weekId (e.g. from admin-defined battle)
  const weekId = entry.weekId || getCurrentWeekId();
  const data = {
    weekId,
    uid:         user ? user.uid         : 'guest',
    userName:    user ? (user.displayName || user.email.split('@')[0]) : 'Anonymous',
    imageUrl:    entry.imageUrl    || '',
    prompt:      entry.prompt      || '',
    vibe:        entry.vibe        || '',
    productType: entry.productType || 'tshirt',
    color:       entry.color       || 'white',
    votes:       0,
    wins:        0,
    losses:      0,
    round:       1,
    status:      'active',   // active | champion | eliminated
    createdAt:   serverTimestamp(),
  };
  const ref = await addDoc(collection(db, 'battle_entries'), data);
  return ref.id;
}

/** Get all active entries for the current week */
async function getWeekEntries(weekId) {
  weekId = weekId || getCurrentWeekId();
  const q = query(
    collection(db, 'battle_entries'),
    where('weekId', '==', weekId),
    where('status', '==', 'active'),
    orderBy('votes', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Get all entries including champions across all weeks */
async function getAllEntries() {
  const q = query(
    collection(db, 'battle_entries'),
    orderBy('createdAt', 'desc'),
    limit(200)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Get Chaos Champions (all-time winners) */
async function getChampions() {
  const q = query(
    collection(db, 'battle_entries'),
    where('status', '==', 'champion'),
    orderBy('votes', 'desc'),
    limit(20)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Cast a vote for a design.
 * Uses a per-user vote record to prevent duplicate votes.
 * voteFor = entry ID the user voted FOR
 * voteAgainst = entry ID the user voted AGAINST (in the matchup)
 */
async function castVote(voteFor, voteAgainst, weekId) {
  weekId = weekId || getCurrentWeekId();
  const user = auth.currentUser;
  // Use fingerprint for guest voters
  const voterId = user ? user.uid : await _getGuestId();
  const voteKey = `${weekId}_${voteFor}_${voteAgainst}_${voterId}`;
  const voteRef = doc(db, 'battle_votes', voteKey);

  // Check if already voted this matchup
  const existing = await getDoc(voteRef);
  if (existing.exists()) {
    throw new Error('already_voted');
  }

  // Record the vote
  await setDoc(voteRef, {
    weekId, voterId,
    voteFor, voteAgainst,
    createdAt: serverTimestamp(),
  });

  // Increment vote count
  await updateDoc(doc(db, 'battle_entries', voteFor), {
    votes: increment(1)
  });

  return true;
}

/** Check if current user/guest already voted on a matchup */
async function hasVoted(voteFor, voteAgainst, weekId) {
  weekId = weekId || getCurrentWeekId();
  const user = auth.currentUser;
  const voterId = user ? user.uid : await _getGuestId();
  const voteKey = `${weekId}_${voteFor}_${voteAgainst}_${voterId}`;
  const snap = await getDoc(doc(db, 'battle_votes', voteKey));
  return snap.exists();
}

/** Get current week identifier  e.g. "2025-W11" */
function getCurrentWeekId() {
  // ISO 8601 week: anchored to Jan 4 (same as admin.html _isoWeek)
  // This ensures admin-created battles and submitted entries always share the same weekId.
  const d    = new Date();
  const jan4 = new Date(d.getFullYear(), 0, 4);
  const week = Math.ceil(((d - jan4) / 86400000 + jan4.getDay() + 1) / 7);
  return `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

/** Stable guest fingerprint stored in localStorage */
async function _getGuestId() {
  let id = localStorage.getItem('_cs_guest_id');
  if (!id) {
    id = 'g_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('_cs_guest_id', id);
  }
  return id;
}

/* ── Exports ── */
/* ── Design Image Firestore Fallback ────────────────────────
   When Firebase Storage rules block writes, save the base64
   image directly to Firestore and return a pseudo-URL:
     firestore://design_images/<docId>
   cart.html and orders resolve this back to the data URL.
   ───────────────────────────────────────────────────────── */
async function saveDesignImageToFirestore(dataUrl, { orderId = '', itemId = '', uid = '' } = {}) {
  if (!dataUrl || !dataUrl.startsWith('data:')) return null;
  // Firestore 1MB doc limit — base64 PNG ~200-500KB, fine
  if (dataUrl.length > 900000) {
    console.warn('[Firestore] Image too large for Firestore fallback, skipping');
    return null;
  }
  try {
    const ref = await addDoc(collection(db, 'design_images'), {
      data:      dataUrl,
      orderId:   orderId  || '',  // ties image → order
      itemId:    itemId   || '',  // ties image → cart item
      uid:       uid      || '',  // ties image → user
      createdAt: serverTimestamp(),
    });
    return `firestore://design_images/${ref.id}`;
  } catch (e) {
    console.warn('[Firestore] saveDesignImageToFirestore failed:', e.message);
    return null;
  }
}

async function getDesignImageFromFirestore(firestoreUrl) {
  if (!firestoreUrl || !firestoreUrl.startsWith('firestore://design_images/')) return null;
  const id   = firestoreUrl.replace('firestore://design_images/', '');
  const snap = await getDoc(doc(db, 'design_images', id));
  return snap.exists() ? snap.data().data : null;
}

/* ── Shared Designs ─────────────────────────────────────────
   saveSharedDesign  — stores design metadata in Firestore
                       returns the short document ID
   getSharedDesign   — fetches it back by ID
   ───────────────────────────────────────────────────────── */
async function saveSharedDesign({ imageUrl, prompt, name, vibe, product }) {
  // Firestore doc limit is 1MB. Base64 PNGs are ~200-500KB — fine.
  // If somehow larger, truncate gracefully (recipient gets broken image, not a crash).
  const safeImg = (imageUrl && imageUrl.length > 900000)
    ? '' // too large — save without image, link still works
    : (imageUrl || '');

  const ref = await addDoc(collection(db, 'shared_designs'), {
    imageUrl:  safeImg,
    prompt:    prompt   || '',
    name:      name     || '',
    vibe:      vibe     || 'bold',
    product:   product  || 'tshirt',
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

async function getSharedDesign(id) {
  const snap = await getDoc(doc(db, 'shared_designs', id));
  return snap.exists() ? snap.data() : null;
}

export {
  auth, db, storage,
  onAuthStateChanged,
  signUp, signIn, signInGoogle, handleGoogleRedirect, logOut,
  getUserProfile, updateUserProfile,
  isAdmin, promoteToAdmin,
  uploadDesignImage,
  saveOrder, getUserOrders, getAllOrders,
  updateOrderStatus, updateOrderFields,
  getAllUsers,
  submitBattleEntry, getWeekEntries, getAllEntries, getChampions,
  castVote, hasVoted, getCurrentWeekId,
  saveSharedDesign, getSharedDesign,
  saveDesignImageToFirestore, getDesignImageFromFirestore,
  getGenCredits, consumeGenCredit, grantGenCreditsAfterOrder,
};
